import { Container, Employee, Shop } from "@/types/v2";
import { Fragment, useMemo } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/Base/Tooltip";
import { Icon } from "@/components/Common/Icon";
import { formatLabels, formatTechnicianNames } from "@/lib/cards";
import { useFeatures } from "@/hooks/useFeatures";
import { useStore } from "@/stores/useStore";
import { ClassNameValue } from "tailwind-merge";
import { cn } from "@/lib/utils";
import { getContainerHeaderTextClasses } from "@/lib/containerHeader";
import { DEFAULT_SHOW_CONTAINER_LINK_ICONS } from "@/lib/const";

const IconClassName: ClassNameValue = "w-3 align-middle h-full";

export type ContainerHeaderProps = {
  container?: Container;
};
export const ContainerHeader = ({ container }: ContainerHeaderProps) => {
  const { shop, employees } = useStore();
  const compact = useStore((s) => s.dashboardCompactContainerHeaders);
  const titleClasses = getContainerHeaderTextClasses(compact);
  // Off unless the shop opts back in — the linkage these icons flag is set (and
  // still shown) in Layout Builder, so on the board they only cost header room.
  const showLinkIcons =
    shop?.shopConfig?.showContainerLinkIcons ??
    DEFAULT_SHOW_CONTAINER_LINK_ICONS;

  const displayName = useMemo(
    () => container?.displayName ?? container?.id ?? "Container",
    [container]
  );

  if (!container)
    return (
      <div className="sticky top-0 z-10 flex w-full flex-row items-center justify-start pb-1">
        <p
          className={cn(
            "cursor-default overflow-hidden text-ellipsis whitespace-nowrap text-nowrap",
            titleClasses
          )}
          title={displayName}
        >
          {displayName}
        </p>
      </div>
    );

  return (
    <div className="sticky top-0 z-10 flex w-full flex-row items-center justify-start p-1">
      <p
        className={cn(
          "min-w-0 cursor-default overflow-hidden text-ellipsis whitespace-nowrap text-nowrap",
          titleClasses
        )}
        title={displayName}
      >
        {displayName}
      </p>
      <span
        className={cn(
          "ml-2 shrink-0 whitespace-nowrap text-gray-400",
          compact ? "text-[11px]" : "text-sm"
        )}
      >
        ({container.tags.length})
      </span>
      <div className="flex-grow" />

      <div className="flex flex-row gap-1 self-start text-gray-400">
        {shop && showLinkIcons && (
          <Fragment>
            <LinkedSourceLabel container={container} shop={shop} />
            <LinkedTechnician
              container={container}
              employees={employees}
              shop={shop}
            />
          </Fragment>
        )}
      </div>
    </div>
  );
};

const LinkedSourceLabel = ({
  container,
  shop,
}: {
  container: Container;
  shop: Shop;
}) => {
  const { features } = useFeatures(shop);
  const labelLinkingEnabled = useMemo(
    () => features.isROLabelLinkEnabled,
    [features]
  );
  const hasLinkedLabel = useMemo(() => {
    if (!container.linkedSourceLabel) return false;
    if (typeof container.linkedSourceLabel === "string")
      return (
        container.linkedSourceLabel.length > 0 &&
        container.linkedSourceLabel !== " "
      );
    return (
      container.linkedSourceLabel && container.linkedSourceLabel.length > 0
    );
  }, [container.linkedSourceLabel]);

  const linkedSourceLabel = useMemo(() => {
    if (hasLinkedLabel) {
      return container.linkedSourceLabel as string | string[];
    }
    return [];
  }, [container.linkedSourceLabel, hasLinkedLabel]);

  if (!labelLinkingEnabled || !hasLinkedLabel) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger>
        <Icon name="linkedLabel" className={IconClassName} />
      </TooltipTrigger>
      <TooltipContent>
        {`Linked to ${formatLabels(linkedSourceLabel)}`}
      </TooltipContent>
    </Tooltip>
  );
};

const LinkedTechnician = ({
  container,
  employees,
  shop,
}: {
  container: Container;
  employees?: Employee[];
  shop: Shop;
}) => {
  const { features } = useFeatures(shop);
  const technicianLinkingEnabled = useMemo(
    () => features.isTechnicianLinkEnabled,
    [features]
  );
  const hasLinkedTechnician = useMemo(() => {
    if (!container.linkedTechnicianId) return false;
    if (typeof container.linkedTechnicianId === "string")
      return (
        container.linkedTechnicianId.length > 0 &&
        container.linkedTechnicianId !== " "
      );
    if (Array.isArray(container.linkedTechnicianId))
      return container.linkedTechnicianId.length > 0;
    return `${container.linkedTechnicianId}`?.length > 0;
  }, [container.linkedTechnicianId]);

  const linkedTechnicianId = useMemo(() => {
    if (hasLinkedTechnician) {
      return container.linkedTechnicianId as string | string[];
    }
    return [];
  }, [container.linkedTechnicianId, hasLinkedTechnician]);

  if (!hasLinkedTechnician || !employees || !technicianLinkingEnabled)
    return null;

  return (
    <Tooltip>
      <TooltipTrigger>
        <Icon name="user" className={IconClassName} />
      </TooltipTrigger>
      <TooltipContent>
        {`Linked to ${formatTechnicianNames(linkedTechnicianId, employees)}`}
      </TooltipContent>
    </Tooltip>
  );
};
